import { ONE_YEAR_IN_S } from './constants';
import capitalize from './utils/capitalize';
import { formatDateTimeHumanFriendly } from './utils/formatters';

const pattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
export const isValidEmailAddress = emailAddress => {
  return pattern.test(emailAddress);
};

export const withTwoDigits = number => {
  if (String(number).length === 1) return `0${number}`;

  return number;
};

export const calculateTotal = (unitPrice, slots, discount, vat) => {
  let total = (unitPrice * slots - (discount ? unitPrice * slots * (discount / 100) : 0)).toFixed(2);
  return total;
};

export const formatCurrency = amount => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const calculateSubscriptionTotal = subscriptions => {
  let total = 0;
  subscriptions.forEach(subs => {
    const { subscription = {} } = subs;
    if (subscription.status !== 'cancelled') {
      total += subscription.subscription_items[0]?.unit_price * subscription.subscription_items[0]?.quantity;
    }
  });

  return formatCurrency(total / 100);
};

export const getCommissionsValue = (globalValue, organisationValue) => {
  if (!isNaN(organisationValue) && !isNaN(parseFloat(organisationValue))){
    return organisationValue;
  }
  return `${globalValue} (Global)`;
};

export const getPercentageDisplayWithGlobal = (globalValue, organisationValue) => {
  if (!isNaN(organisationValue) && !isNaN(parseFloat(organisationValue))){
    return `${(parseFloat(organisationValue) * 100).toFixed(0)}`;
  }
  return `${(parseFloat(globalValue || 0) * 100).toFixed(0)} (Global)`;
};

export const convertPercentageToDecimal = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return parseFloat(value) / 100;
};

export const convertPercentageField = (data, fieldPath, value) => {
  if (value !== null && value !== undefined && value !== '') {
    data[fieldPath] = parseFloat(value) / 100;
  }
  return data;
};

export const formatDate = (date, locale = 'en-GB') => {
  date = new Date(date);
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const getRandomKey = () => Math.floor(Math.random() * 1000);

export const formatDateToString = date => {
  const appendLeadingZeroes = n => (n <= 9 ? `0${n}` : n);
  const initDate = new Date(date);

  return `${initDate.getFullYear()}-${appendLeadingZeroes(
    initDate.getMonth() + 1
  )}-${appendLeadingZeroes(initDate.getDate())}`;
};

export const formatDateFromString = date => new Date(date).toISOString();

export const validated = validationArray => {
  const isTrue = value => {
    if (value === undefined) value = true;
    return value === true;
  };

  return Object.keys(validationArray)
    .map(key => validationArray[key])
    .map(fields => (Object.keys(fields).length ? Object.values(fields).every(isTrue) : fields))
    .every(isTrue);
};

export const inTrialPeriod = pharmacy => {
  let trialEndDate;
  const { legacy, currentContract: { trialPeriod, startDate: contractStartDate } = {} } = pharmacy;
  if (legacy) return false;
  // Calculate the trial end date
  const startDate = new Date(contractStartDate);
  const daysInStartMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate();
  const trialPeriodInDays = daysInStartMonth * trialPeriod;
  trialEndDate = new Date(startDate.setDate(startDate.getDate() + trialPeriodInDays));

  return new Date() < trialEndDate;
};

export const getOrgType = mainTabValue => {
  switch (mainTabValue) {
    case 0:
      return 'active_org_active_sites';
    case 1:
      return 'active_org_inactive_sites';
    case 2:
      return 'active_org_no_sites';
    case 3:
      return 'pending';
    case 4:
      return 'deactivated';
    case 5:
      return 'payment_overdue';
    default:
      return '';
  }
};

export const getTabValueForOrgType = orgType => {
  switch (orgType) {
    case 'active_org_active_sites':
      return 0;
    case 'active_org_inactive_sites':
      return 1;
    case 'active_org_no_sites':
      return 2;
    case 'pending':
      return 3;
    case 'deactivated':
      return 4;
    case 'payment_overdue':
      return 5;
    default:
      return false;
  }
};


export const getPreviousPageNameByUrl = url => {
  const urls = url.split('/');
  const previousPageName = getLastNonIdElement(urls.slice(0, -1));

  return capitalize(previousPageName.replace(/-/g, ' '));
};

const uuidRegexp = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;

function getLastNonIdElement(items) {
  for (const item of items.reverse()) {
    const isUuid = uuidRegexp.test(item);

    if (!isUuid) {
      return item;
    }
  }
}

export const totalExceeded = orgDetails => {
  const { unitPrice, slots, discount, vat } = orgDetails.currentContract
    ? orgDetails.currentContract
    : '';
  const total = (unitPrice * slots - (discount ? unitPrice * slots * (discount / 100) : 0)).toFixed(
    2
  );
  return vat ? total * 1.2 > 5000 : total > 5000;
};

export const getOrderType = order => {
  const { repeat, otc_count } = order;

  let orderType = '';
  const remediesCount = repeat?.remedies_count || null;

  if (remediesCount) orderType += `${remediesCount} Repeat`;
  if (otc_count) orderType += `\n${otc_count} GSL`;

  return orderType;
};

export const getOrderFulfillmentInfo = order => {
  const fulfillmentInfo = {
    type: null,
    timeslot: null
  };

  fulfillmentInfo.type = 'Collection';

  if (order.delivery) {
    fulfillmentInfo.type = 'Delivery';

    const { active: paidDeliveryActive, free_paid_delivery_age: freePaidDeliveryAge } = order.pharmacy.paid_delivery || {}
    const { birthday } = order?.patient || {};

    if (paidDeliveryActive) {
      const patientAge = birthday / ONE_YEAR_IN_S;

      if (patientAge >= freePaidDeliveryAge) {
        fulfillmentInfo.type = 'Delivery';
      } else {
        fulfillmentInfo.type = 'Paid delivery';
      }
    }

    const { timeslot } = order.delivery;

    if (timeslot && fulfillmentInfo.type !== 'Collection') {
      const start = formatDateTimeHumanFriendly(timeslot.start * 1000);
      const end = new Date(timeslot.end * 1000);

      fulfillmentInfo.timeslot = `${start}-${withTwoDigits(end.getHours())}:${withTwoDigits(
        end.getMinutes()
      )}`;
    }
  }

  return fulfillmentInfo;
};

export const cutLongText = (text, max) => {
  if (text.length <= max) return text;

  return text.slice(0, max) + '…';
}

export const isTimeValid = (timeslot) => {
  const times = timeslot.split(':');

  if (times.length !== 2) return false;
  if (times[0] < 0 || times[0] > 23) return false;
  if (times[1] < 0 || times[1] > 60) return false;
  if (times[1].length !== 2) return false;

  return true;
};

export function convertArrayToShortText(categories) {
  if (!categories?.length) return '';

  const visibleCategories = [];
  let index = 0;

  while (categories[index] && `${visibleCategories.join('')}${categories[index]}`.length < 30) {
    visibleCategories.push(categories[index]);
    index++;
  }

  return `${visibleCategories.join(', ')}${index < categories.length ? `, +${categories.length - index}` : ''}`;
}

export const isTimeslotValid = (startTime, endTime) => {
  if (!(isTimeValid(startTime) && isTimeValid(endTime))) return false;

  const start = startTime.split(':');
  const end = endTime.split(':');

  // Check hours
  if (start[0] > end[0]) return false;
  // Check minutes
  if (start[0] === end[0] && start[1] > end[1]) return false;

  return true;
};

export const isCommissionSet = (commission) => {
  switch (commission) {
    case '0':
    case '0.0':
    case '0.00':
      return false;
    default:
      break;
  }

  return !!(commission);
};

export const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
}

export const calculateSlaTimeslot = ({
  slaTimeslot,
  deliveryTimeslot,
  orderData,
}) => {
  const { slaStart, slaEnd } = slaTimeslot || {};
  const formattedTimeslot = { start: null, end: null };

  if (!orderData) {
    if (!slaTimeslot.slaStart) {
      return formattedTimeslot
    }
    formattedTimeslot.start = new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
    }).format(slaStart * 1000);
    formattedTimeslot.end = new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
    }).format(slaEnd * 1000);

  } else {
    const { deliveryStart, deliveryEnd } = deliveryTimeslot;

    const {
      repeat_id,
      otc,
      delivery: { option, isPremium },
    } = orderData;
    const { private_prescription: isPrivatePrescription } = otc || {};

    const isPremiumRepeatDelivery = repeat_id && isPremium;
    const isOtcOnlyCollection = !repeat_id && !option;

    if (isOtcOnlyCollection && !isPrivatePrescription) {
      formattedTimeslot.start = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: 'numeric',
      }).format(slaStart * 1000);

      formattedTimeslot.end = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: 'numeric',
      }).format(slaEnd * 1000);
    } else if (
      deliveryStart &&
      option !== 'postal' &&
      (otc || isPremiumRepeatDelivery)
    ) {
      formattedTimeslot.start = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: 'numeric',
      }).format(deliveryStart * 1000);

      formattedTimeslot.end = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        hour: 'numeric',
        minute: 'numeric',
      }).format(deliveryEnd * 1000);
    } else {
      formattedTimeslot.start = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        month: 'short',
        day: 'numeric',
      }).format(slaStart * 1000);

      formattedTimeslot.end = new Intl.DateTimeFormat('en', {
        timeZone: 'UTC',
        hour12: false,
        month: 'short',
        day: 'numeric',
      }).format(slaEnd * 1000);
    }
  }

  return `${formattedTimeslot.start} - ${formattedTimeslot.end}`;
};

export const isToday = (date) => {
  const today = new Date()
  return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
};

export const getOrderPlaceDate = (timestamp) => {
  const date = new Date(timestamp * 1000);

  if (isToday(date)) {
    return `Today, ${new Intl.DateTimeFormat('en', { hour: 'numeric', minute: 'numeric' }).format(date)}`
  }

  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(date);
};

export const setAllObjectFieldsToValue = (object, value) => {
  return Object.keys(object).reduce((summaryObject, currentField) => {
    summaryObject[currentField] = value;
    return summaryObject;
  }, object);
};

export const getArrayDiferences = (firstArray, secondArray) => {
  return firstArray.filter((arrayItem) => !secondArray.includes(arrayItem));
};
